// ---- Brewing (client, plan 18 CP3) --------------------------------------------
// The PASSIVE branch of Cooking (the active Cooking loop lives in cooking.ts).
// It mirrors Hunting's Trapping / Fishing's Netting exactly: with a fermenter in
// the bag, on dry ground, the Ferment slot anchors it (`deployBrew`); the server
// owns the durable soak timer and everyone sees the cask (the `brews`/`brew`
// mirror below). Return once ready and the same slot reads Collect. No camera,
// no input lock — relaxed. The brewed goods (ale/preserves) are food; eat them
// through the cooking.ts consume path.
//
// This module has no keydown/pointerdown of its own (passive — the slot button +
// its combat.ts render are the whole surface), so it needs no init ordering
// against focus.ts; game.ts just wires it after initCooking.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { heightAt, WATER_LEVEL } from "./terrain.ts";
import { ITEMS } from "./items/index.ts";
import { isBrew, buildItem, type ItemSpec } from "./items/define.ts";
import { countOf } from "./inventory.ts";
import { sendDeployBrew, sendCollectBrew } from "./net.ts";
import { nearRange } from "./cooking.ts";
import { WATER_MARGIN } from "./sim/fishing.ts";
import { COOKING_TUNING as CT } from "./tunables.ts";
import type { ItemType, BrewDeploy } from "./shared.ts";

let scene: THREE.Scene;

// ---- Deployed-fermenter mirror (server-owned; everyone sees the cask) ----------
const brewsMap = new Map<string, BrewDeploy>();
const brewGroups = new Map<string, THREE.Group>();

// The cask model: the first fermenter item's own build (one type, like the trap
// snare / net floats being generic — the wire doesn't carry the item type).
let brewModel: ItemSpec | null | undefined;
function buildBrewGroup(): THREE.Group {
  brewModel ??= ITEMS.find(isBrew) ?? null;
  return brewModel ? buildItem(brewModel) : new THREE.Group();
}

function addBrewGroup(b: BrewDeploy): void {
  const g = buildBrewGroup();
  g.position.set(b.x, heightAt(b.x, b.z), b.z);
  scene.add(g);
  brewGroups.set(b.id, g);
}

function removeBrewGroup(id: string): void {
  const g = brewGroups.get(id);
  if (g) scene.remove(g);
  brewGroups.delete(id);
}

/** Full sync (join/reconnect): replace the whole mirror. */
export function setBrews(list: BrewDeploy[]): void {
  for (const id of [...brewGroups.keys()]) removeBrewGroup(id);
  brewsMap.clear();
  for (const b of list) {
    brewsMap.set(b.id, b);
    addBrewGroup(b);
  }
}

/** One fermenter deployed/collected (broadcast, personalized `mine`). */
export function applyBrewDelta(op: "add" | "remove", brew?: BrewDeploy, id?: string): void {
  if (op === "add" && brew) {
    brewsMap.set(brew.id, brew);
    removeBrewGroup(brew.id); // belt-and-braces against a replayed add
    addBrewGroup(brew);
  } else if (op === "remove" && id) {
    brewsMap.delete(id);
    removeBrewGroup(id);
  }
}

// ---- Shared little helpers ------------------------------------------------------

/** Some other activity owns the input (the shared lock — brewing has no active
 *  flag of its own to exclude, so the plain predicate suffices). */
function busy(): boolean {
  return inputLocked();
}

/** Dry ground (the deploy validity — the server checks the same). */
function isDry(x: number, z: number): boolean {
  return heightAt(x, z) >= WATER_LEVEL - WATER_MARGIN;
}

/** First dry point straight ahead within reach (where the cask drops). The slot
 *  ACTION uses this fresh; the per-frame slot state goes through the cache below. */
function forwardDryPoint(max: number): { x: number; z: number } | null {
  const p = game.player.position;
  const ry = game.player.rotation.y;
  const dx = Math.sin(ry), dz = Math.cos(ry);
  for (let d = 1.5; d <= max; d += 1) {
    const x = p.x + dx * d, z = p.z + dz * d;
    if (isDry(x, z)) return { x, z };
  }
  return null;
}

// heightAt() samples are 4-octave noise and brewSlotState asks EVERY frame while
// a fermenter is carried — cache with a short TTL, invalidated by moving/turning
// (the fishing.ts cachedForwardWater discipline).
const dryProbe = { at: 0, x: 0, z: 0, ry: 0, result: null as { x: number; z: number } | null };
function cachedForwardDry(max: number): { x: number; z: number } | null {
  const now = performance.now();
  const p = game.player.position, ry = game.player.rotation.y;
  if (now - dryProbe.at < 200 && Math.abs(ry - dryProbe.ry) < 0.1 && Math.hypot(p.x - dryProbe.x, p.z - dryProbe.z) < 0.5) {
    return dryProbe.result;
  }
  Object.assign(dryProbe, { at: now, x: p.x, z: p.z, ry, result: forwardDryPoint(max) });
  return dryProbe.result;
}

// The deployable fermenter types, resolved once (content is static).
let brewTypes: ItemType[] | null = null;

/** Any deployable fermenter we're carrying (first match — CP3 has one type). */
function carriedBrewType(): ItemType | null {
  brewTypes ??= ITEMS.filter(isBrew).map((s) => s.type);
  for (const type of brewTypes) {
    if (countOf(type) > 0) return type;
  }
  return null;
}

/** The nearest of OUR deployed fermenters within reach, or null. */
function nearestOwnBrew(): BrewDeploy | null {
  const p = game.player.position;
  let best: BrewDeploy | null = null, bd = Infinity;
  for (const b of brewsMap.values()) {
    if (!b.mine) continue;
    const d = Math.hypot(p.x - b.x, p.z - b.z);
    if (d <= CT.brewRange && d < bd) { bd = d; best = b; }
  }
  return best;
}

// ---- Action-bar slot state (combat.ts owns the bar and renders these) ------------

/** The Ferment / Collect slot: collect wins when one of our fermenters is ready
 *  in reach; otherwise it shows whenever a fermenter is carried on dry ground. */
export function brewSlotState(): { show: boolean; enabled: boolean; label: string; icon: string } {
  if (busy()) return { show: false, enabled: false, label: "", icon: "" };
  const own = nearestOwnBrew();
  if (own) {
    const left = own.readyAt - Date.now();
    if (left <= 0) return { show: true, enabled: true, label: "Collect", icon: "🛢️" };
    return { show: true, enabled: false, label: `Brewing ${Math.ceil(left / 60_000)}m`, icon: "🛢️" };
  }
  const spot = carriedBrewType() ? cachedForwardDry(3) : null;
  if (spot) {
    // Plan 25: fermenters are KITCHEN equipment — the pot must sit by a range.
    if (nearRange(spot.x, spot.z)) return { show: true, enabled: true, label: "Ferment", icon: "🛢️" };
    return { show: true, enabled: false, label: "Needs range", icon: "🛢️" };
  }
  return { show: false, enabled: false, label: "", icon: "" };
}

/** Brew slot click (Ferment / Collect). */
function brewSlotAction(): void {
  if (busy()) return;
  const own = nearestOwnBrew();
  if (own) {
    if (Date.now() >= own.readyAt) sendCollectBrew(own.id);
    return;
  }
  const type = carriedBrewType();
  const spot = type ? forwardDryPoint(3) : null;
  if (!type || !spot || !nearRange(spot.x, spot.z)) return; // kitchen-gated (plan 25)
  sendDeployBrew(type, spot.x, spot.z);
}

// ---- Init -------------------------------------------------------------------------

export function initBrewing(s: THREE.Scene): void {
  scene = s;
  document.getElementById("brewBtn")?.addEventListener("click", brewSlotAction);
}
